"use client";

import { m as motion, useReducedMotion } from "framer-motion";
import { useEffect, useMemo, useRef, useState } from "react";

/**
 * Micro-motivi animati line-art (ciano) per le schede della pagina Servizi.
 * Una variante per servizio. Solo transform/opacity (+ un rAF per il 3D),
 * reduced-motion-aware. Decorativi: aria-hidden.
 */

const S = "hsl(var(--primary))";
const PHI = 1.618;

/** Wireframe 3D che ruota e muta forma: cubo → ottaedro → icosaedro. */
const SHAPES: number[][][] = [
  [
    [-1, -1, -1], [1, -1, -1], [1, 1, -1], [-1, 1, -1],
    [-1, -1, 1], [1, -1, 1], [1, 1, 1], [-1, 1, 1],
  ],
  [
    [1, 0, 0], [-1, 0, 0], [0, 1, 0], [0, -1, 0], [0, 0, 1], [0, 0, -1],
  ],
  [
    [0, 1, PHI], [0, 1, -PHI], [0, -1, PHI], [0, -1, -PHI],
    [1, PHI, 0], [1, -PHI, 0], [-1, PHI, 0], [-1, -PHI, 0],
    [PHI, 0, 1], [PHI, 0, -1], [-PHI, 0, 1], [-PHI, 0, -1],
  ],
];

function Morph3DScene({ animate }: { animate: boolean }) {
  const [idx, setIdx] = useState(0);
  const lineRefs = useRef<Array<SVGLineElement | null>>([]);

  const { verts, edges } = useMemo(() => {
    const raw = SHAPES[idx];
    const maxR = Math.max(...raw.map((p) => Math.hypot(p[0], p[1], p[2])));
    const v = raw.map((p) => [p[0] / maxR, p[1] / maxR, p[2] / maxR]);
    let min = Infinity;
    for (let i = 0; i < v.length; i++)
      for (let j = i + 1; j < v.length; j++) {
        const d = Math.hypot(v[i][0] - v[j][0], v[i][1] - v[j][1], v[i][2] - v[j][2]);
        if (d < min) min = d;
      }
    const e: Array<[number, number]> = [];
    for (let i = 0; i < v.length; i++)
      for (let j = i + 1; j < v.length; j++) {
        const d = Math.hypot(v[i][0] - v[j][0], v[i][1] - v[j][1], v[i][2] - v[j][2]);
        if (d < min * 1.08) e.push([i, j]);
      }
    return { verts: v, edges: e };
  }, [idx]);

  useEffect(() => {
    if (!animate) return;
    const id = window.setTimeout(() => setIdx((i) => (i + 1) % SHAPES.length), 3400);
    return () => window.clearTimeout(id);
  }, [idx, animate]);

  useEffect(() => {
    let raf = 0;
    let ax = 0.5;
    let ay = 0.5;
    const draw = () => {
      const cosX = Math.cos(ax), sinX = Math.sin(ax);
      const cosY = Math.cos(ay), sinY = Math.sin(ay);
      const proj = verts.map(([x, y, z]) => {
        let X = x * cosY - z * sinY;
        let Z = x * sinY + z * cosY;
        const Y = y * cosX - Z * sinX;
        Z = y * sinX + Z * cosX;
        const k = 1 / (3 - Z);
        return [28 + X * k * 48, 28 + Y * k * 48];
      });
      for (let i = 0; i < edges.length; i++) {
        const ln = lineRefs.current[i];
        if (!ln) continue;
        const a = proj[edges[i][0]];
        const b = proj[edges[i][1]];
        ln.setAttribute("x1", a[0].toFixed(2));
        ln.setAttribute("y1", a[1].toFixed(2));
        ln.setAttribute("x2", b[0].toFixed(2));
        ln.setAttribute("y2", b[1].toFixed(2));
      }
    };
    if (!animate) {
      draw();
      return;
    }
    const loop = () => {
      ax += 0.006;
      ay += 0.009;
      draw();
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [verts, edges, animate]);

  return (
    <svg viewBox="0 0 56 56" className="h-full w-full" aria-hidden>
      <motion.g
        key={idx}
        stroke={S}
        strokeWidth={1.2}
        fill="none"
        strokeLinecap="round"
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.9 }}
        transition={{ duration: 0.7, ease: "easeInOut" }}
      >
        {edges.map((_, i) => (
          <line key={i} ref={(el) => {lineRefs.current[i] = el;}} />
        ))}
      </motion.g>
    </svg>
  );
}

export function ServiceMotif({ variant }: { variant: string }) {
  const reduce = useReducedMotion();
  const a = !reduce;
  const vb = { viewBox: "0 0 56 56", className: "h-full w-full" } as const;

  if (variant === "rebuild") {
    // no-code fragile (tratteggiato) → prodotto vero solido e strutturato (cross-fade)
    return (
      <svg {...vb} aria-hidden>
        <motion.rect
          x={10}
          y={12}
          width={36}
          height={32}
          rx={4}
          stroke={S}
          fill="none"
          strokeWidth={1.4}
          strokeDasharray="4 4"
          animate={a ? { opacity: [0.9, 0.08, 0.9] } : undefined}
          transition={a ? { duration: 3.6, repeat: Infinity, ease: "easeInOut" } : undefined}
        />
        <motion.g
          stroke={S}
          fill="none"
          strokeWidth={1.5}
          animate={a ? { opacity: [0.08, 0.95, 0.08] } : { opacity: 0.95 }}
          transition={a ? { duration: 3.6, repeat: Infinity, ease: "easeInOut" } : undefined}
        >
          <rect x={10} y={12} width={36} height={32} rx={4} />
          <line x1={10} y1={22} x2={46} y2={22} />
          <line x1={22} y1={22} x2={22} y2={44} />
          <rect x={13} y={15} width={6} height={4} rx={1} fill={S} stroke="none" />
        </motion.g>
      </svg>
    );
  }

  if (variant === "gauge") {
    // ago che pivota dal centro: la punta segue l'arco (keyframe x2/y2), niente transform-origin
    const ang = [-70, -42, -14, 14, 42, 66];
    const nx = ang.map((d) => +(28 + 15 * Math.sin((d * Math.PI) / 180)).toFixed(2));
    const ny = ang.map((d) => +(41 - 15 * Math.cos((d * Math.PI) / 180)).toFixed(2));
    const ticks = [-78, -39, 0, 39, 78].map((d) => {
      const r = (d * Math.PI) / 180;
      return {
        x1: 28 + Math.sin(r) * 20,
        y1: 41 - Math.cos(r) * 20,
        x2: 28 + Math.sin(r) * 16,
        y2: 41 - Math.cos(r) * 16,
        edge: Math.abs(d) > 70,
      };
    });
    return (
      <svg {...vb} aria-hidden>
        <path d="M8 41 A20 20 0 0 1 48 41" stroke={S} fill="none" strokeWidth={1.6} strokeLinecap="round" opacity={0.4} />
        <g stroke={S} strokeLinecap="round">
          {ticks.map((t, i) => (
            <line
              key={i}
              x1={t.x1}
              y1={t.y1}
              x2={t.x2}
              y2={t.y2}
              strokeWidth={t.edge ? 1.8 : 1.2}
              opacity={t.edge ? 0.85 : 0.45}
            />
          ))}
        </g>
        <motion.line
          x1={28}
          y1={41}
          x2={nx[2]}
          y2={ny[2]}
          stroke={S}
          strokeWidth={2.2}
          strokeLinecap="round"
          animate={a ? { x2: nx, y2: ny } : undefined}
          transition={
            a ? { duration: 2.8, repeat: Infinity, repeatType: "reverse", ease: "easeInOut" } : undefined
          }
        />
        <circle cx={28} cy={41} r={3.4} fill={S} />
      </svg>
    );
  }

  if (variant === "cart") {
    return (
      <svg {...vb} aria-hidden>
        <g
          stroke={S}
          fill="none"
          strokeWidth={1.6}
          strokeLinecap="round"
          strokeLinejoin="round"
          opacity={0.9}
        >
          {/* carrello: maniglia, cestello, ruote */}
          <path d="M8 12 L14 12 L18 33 L41 33 L45 18 L16 18" />
          <circle cx={21} cy={41} r={2.6} />
          <circle cx={39} cy={41} r={2.6} />
        </g>
        {/* prodotto che cade nel cestello */}
        <motion.circle
          cx={30}
          r={2.8}
          fill={S}
          animate={a ? { cy: [6, 27], opacity: [0, 1, 1, 0] } : { cy: 27 }}
          transition={
            a
              ? { duration: 2, repeat: Infinity, ease: "easeIn", times: [0, 0.55, 0.8, 1] }
              : undefined
          }
        />
      </svg>
    );
  }

  if (variant === "code") {
    // finestra app/browser: barra con pallini + righe di contenuto (una "scrive")
    return (
      <svg {...vb} aria-hidden>
        <rect x={8} y={11} width={40} height={34} rx={3} stroke={S} fill="none" strokeWidth={1.5} opacity={0.9} />
        <line x1={8} y1={20} x2={48} y2={20} stroke={S} strokeWidth={1.2} opacity={0.6} />
        <g fill={S} opacity={0.7}>
          <circle cx={13} cy={15.5} r={1.3} />
          <circle cx={17.5} cy={15.5} r={1.3} />
          <circle cx={22} cy={15.5} r={1.3} />
        </g>
        <motion.line
          x1={14}
          y1={29}
          x2={14}
          y2={29}
          stroke={S}
          strokeWidth={1.6}
          strokeLinecap="round"
          animate={a ? { x2: [14, 36] } : { x2: 36 }}
          transition={a ? { duration: 1.6, repeat: Infinity, repeatType: "reverse", ease: "easeInOut" } : undefined}
        />
        <line x1={14} y1={35} x2={42} y2={35} stroke={S} strokeWidth={1.6} strokeLinecap="round" opacity={0.45} />
        <line x1={14} y1={40} x2={30} y2={40} stroke={S} strokeWidth={1.6} strokeLinecap="round" opacity={0.45} />
      </svg>
    );
  }

  if (variant === "phone") {
    return (
      <svg {...vb} aria-hidden>
        <rect x={20} y={8} width={16} height={32} rx={3} stroke={S} fill="none" strokeWidth={1.4} opacity={0.85} />
        <line x1={25} y1={36} x2={31} y2={36} stroke={S} strokeWidth={1.4} opacity={0.55} />
        <motion.circle
          cx={28}
          cy={20}
          r={5}
          fill="none"
          stroke={S}
          strokeWidth={1.2}
          style={{ transformBox: "fill-box", transformOrigin: "center" }}
          animate={a ? { scale: [0.5, 1.7], opacity: [0.8, 0] } : undefined}
          transition={a ? { duration: 2, repeat: Infinity, ease: "easeOut" } : undefined}
        />
        <circle cx={28} cy={20} r={2} fill={S} />
      </svg>
    );
  }

  if (variant === "nodes") {
    const sat: Array<[number, number]> = [
      [12, 14],
      [44, 16],
      [16, 44],
      [42, 42],
    ];
    return (
      <svg {...vb} aria-hidden>
        <g stroke={S} strokeWidth={1} opacity={0.4} fill="none">
          {sat.map(([x, y], i) => (
            <line key={i} x1={28} y1={28} x2={x} y2={y} />
          ))}
        </g>
        <g fill={S}>
          <circle cx={28} cy={28} r={3.4} />
          {sat.map(([x, y], i) => (
            <motion.circle
              key={i}
              cx={x}
              cy={y}
              r={2.4}
              animate={a ? { opacity: [0.3, 1, 0.3] } : undefined}
              transition={
                a ? { duration: 2.4, repeat: Infinity, ease: "easeInOut", delay: i * 0.3 } : undefined
              }
            />
          ))}
        </g>
      </svg>
    );
  }

  if (variant === "target") {
    // direzione chiara: la freccia centra il bersaglio
    return (
      <svg {...vb} aria-hidden>
        <g stroke={S} fill="none">
          <circle cx={28} cy={28} r={20} opacity={0.28} />
          <circle cx={28} cy={28} r={13} opacity={0.45} />
          <circle cx={28} cy={28} r={6} opacity={0.6} />
        </g>
        <motion.g
          stroke={S}
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
          animate={a ? { x: [-16, 0, 0, 0], y: [-16, 0, 0, 0], opacity: [0, 1, 1, 0] } : { opacity: 1 }}
          transition={
            a ? { duration: 2.6, repeat: Infinity, ease: "easeIn", times: [0, 0.5, 0.82, 1] } : undefined
          }
        >
          <line x1={14} y1={14} x2={27} y2={27} />
          <path d="M27 20 L27 27 L20 27" />
        </motion.g>
        <motion.circle
          cx={28}
          cy={28}
          r={2.8}
          fill={S}
          style={{ transformBox: "fill-box", transformOrigin: "center" }}
          animate={a ? { scale: [1, 1.6, 1] } : undefined}
          transition={a ? { duration: 2.6, repeat: Infinity, ease: "easeOut" } : undefined}
        />
      </svg>
    );
  }

  if (variant === "cycle") {
    // manutenzione: due frecce che girano attorno a un punto fisso (il sito resta su)
    return (
      <svg {...vb} aria-hidden>
        <motion.g
          stroke={S}
          strokeWidth={1.6}
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
          style={{ transformBox: "view-box", transformOrigin: "28px 28px" }}
          animate={a ? { rotate: 360 } : undefined}
          transition={a ? { duration: 6, repeat: Infinity, ease: "linear" } : undefined}
        >
          <path d="M44 28 A16 16 0 0 1 16.7 39.3" />
          <path d="M16.7 39.3 L17 33 M16.7 39.3 L23 39" />
          <path d="M12 28 A16 16 0 0 1 39.3 16.7" />
          <path d="M39.3 16.7 L39 23 M39.3 16.7 L33 17" />
        </motion.g>
        <circle cx={28} cy={28} r={3.4} fill={S} />
      </svg>
    );
  }

  // 3D lab: forma che ruota e muta (cubo → ottaedro → icosaedro)
  return <Morph3DScene animate={a} />;
}
